/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */

import clsx from 'clsx';
import React, { useState, DragEvent, useEffect, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import { useLocation } from 'react-router-dom';

import { SidebarTextPanel } from './sidebar-text-panel';
import { APP_WIDGET_NAMES, CARD_TYPES, ONBOARDING_STEPS, WIDGET_TYPES } from '../../types';
import { useReportBuilderContext } from '../../report-builder.context';
import { TemplatesPanel } from './templates-panel';
import { WidgetsPanel } from './widgets-panel';
import { SchedulePanel } from './schedule-panel';
import { SidebarTagsPanel } from './sidebar-tags-panel';
import { LayoutItem as Layout } from '../grid-layout';
import { SidebarImagesPanel } from './sidebar-images-panel';
import { SidebarMenu } from './sidebar-menu/sidebar-menu';
import { SIDEBAR_ITEM_TYPES } from './sidebar-menu/types';
import { OnboardingTemplatesPanel } from '../onboarding/sidebar-panels/onboarding-templates-panel/onboarding-templates-panel';
import { OnboardingWidgetsPanel } from '../onboarding/sidebar-panels/onboarding-widgets-panel/onboarding-widgets-panel';
import { useOnboardingContext } from '../../onboarding.context';
import { TOP_BAR_HEIGHT, WIDTH_STEPS } from '../../constants';
import { BuilderPanel } from './builder-panel/builder-panel';
import { useNavHeight } from '../../../hooks/useNavHeight';

import styles from './sidebar.scss';

const TOP_OFFSET = 58;

interface Props {
  sideBarOpenChange?: (s: boolean) => void;
  style?: React.CSSProperties;
}

export const Sidebar = ({ sideBarOpenChange, style }: Props) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [activeItem, setActiveItem] = useState<SIDEBAR_ITEM_TYPES | undefined>(SIDEBAR_ITEM_TYPES.TEMPLATES);
  const { currentCard, isOnboarding, isTemplate, isMasterPage, isViewPage } = useReportBuilderContext();
  const { currentStep } = useOnboardingContext();
  const { navHeight } = useNavHeight();
  const routerLocation = useLocation();
  const cameFromOnboarding = useRef(!!routerLocation.state?.cameFromOnboarding);

  const topOffset = navHeight + (isMasterPage ? 0 : TOP_OFFSET);
  const sidebarHeight = `calc(100vh - ${navHeight + TOP_BAR_HEIGHT}px)`;

  const onItemClick = (type: SIDEBAR_ITEM_TYPES) => {
    if (!isOnboarding) {
      setActiveItem(type);
    }
  };

  const onToggleSidebarContent = (e: React.MouseEvent<HTMLElement>, isOpen: boolean) => {
    e.stopPropagation();
    setIsSidebarOpen(isOpen);
    sideBarOpenChange?.(isOpen);
  };

  const onDragStart = (
    e: DragEvent<HTMLDivElement | HTMLSpanElement>,
    type: CARD_TYPES,
    data?: Record<string, unknown>
  ) => {
    let item: Layout = {
      i: uuid(),
      w:
        data?.widgetType === WIDGET_TYPES.TABLE ||
        [APP_WIDGET_NAMES.RATIOS, APP_WIDGET_NAMES.CUSTOMERS_AND_SUPPLIERS].includes(
          (data?.name || '') as APP_WIDGET_NAMES
        )
          ? WIDTH_STEPS
          : WIDTH_STEPS / 2 - 1,
      h: data?.widgetType === WIDGET_TYPES.DATA_CARD ? 15 : 40,
      x: 0,
      y: 0
    };
    switch (type) {
      case CARD_TYPES.TEXT:
        item = { ...item, h: 15 };
        break;
      case CARD_TYPES.APP_WIDGET:
        item = {
          ...item,
          h: [
            APP_WIDGET_NAMES.CASH_IN_FROM_INVOICES,
            APP_WIDGET_NAMES.CASH_OUT_FROM_BILLS,
            APP_WIDGET_NAMES.TOP_DEBT_BALANCES
          ].includes((data?.name || '') as APP_WIDGET_NAMES)
            ? 25
            : 40
        };
        break;
      default:
        item = { ...item, minH: 5, minW: 2 };
        break;
    }
    e.dataTransfer?.setData('itemData', JSON.stringify({ item, data, type }));
  };

  const onDragTemplateStart = (e: DragEvent<HTMLDivElement | HTMLSpanElement>, templateId: string) => {
    e.dataTransfer?.setData('itemData', JSON.stringify({ templateId, data: {}, type: 'template' }));
  };

  // Update the active sidebar item when relevant data changes
  useEffect(() => {
    if (isOnboarding) {
      if (currentStep === ONBOARDING_STEPS.ADD_TEMPLATE) {
        setActiveItem(SIDEBAR_ITEM_TYPES.TEMPLATES);
      }
      if (currentStep === ONBOARDING_STEPS.ADD_WIDGET) {
        setActiveItem(SIDEBAR_ITEM_TYPES.WIDGETS);
      }
      if (currentStep === ONBOARDING_STEPS.SELECT_WIDGET) {
        setActiveItem(SIDEBAR_ITEM_TYPES.BUILDER);
      }
      if (currentStep === ONBOARDING_STEPS.ADD_TEXT_IMAGES) {
        setActiveItem(undefined);
      }
      if (currentStep === ONBOARDING_STEPS.SEND_OR_SCHEDULE) {
        setActiveItem(SIDEBAR_ITEM_TYPES.SCHEDULE);
      }
      if (currentStep === ONBOARDING_STEPS.SEE_HELP) {
        setActiveItem(undefined);
      }
    } else {
      if (isMasterPage) {
        setActiveItem(SIDEBAR_ITEM_TYPES.TEXT);
      }
      if (isViewPage) {
        setActiveItem(SIDEBAR_ITEM_TYPES.SCHEDULE);
      }
      if (currentCard?.type === CARD_TYPES.WIDGET) {
        setActiveItem(SIDEBAR_ITEM_TYPES.BUILDER);
      }
      if (currentCard?.type === CARD_TYPES.APP_WIDGET) {
        setActiveItem(SIDEBAR_ITEM_TYPES.WIDGETS);
      }
      if (currentCard?.type === CARD_TYPES.TEXT) {
        setActiveItem(SIDEBAR_ITEM_TYPES.TEXT);
      }
      if (currentCard?.type === CARD_TYPES.IMAGE) {
        setActiveItem(SIDEBAR_ITEM_TYPES.IMAGES);
      }
    }
  }, [currentCard, currentStep, isOnboarding, isViewPage, isMasterPage]);

  // On first load, if the user came from onboarding, close the sidebar
  useEffect(() => {
    if (cameFromOnboarding.current) {
      setIsSidebarOpen(false);
      sideBarOpenChange?.(false);
      cameFromOnboarding.current = false;
    }
  }, [sideBarOpenChange]);

  return (
    <div
      className={clsx(
        styles.sidebar,
        isTemplate && styles.templateColor,
        !isSidebarOpen && styles.sidebarClosed,
        isMasterPage && styles.onMasterPage
      )}
      style={{ top: topOffset, height: sidebarHeight, minHeight: sidebarHeight, ...style }}
    >
      <SidebarMenu
        activeItem={activeItem}
        isOpen={isSidebarOpen}
        onItemClick={onItemClick}
        onToggleSidebar={onToggleSidebarContent}
      />
      <div
        className={clsx(styles.sidebarContent)}
        style={{
          padding: activeItem === SIDEBAR_ITEM_TYPES.BUILDER || activeItem === SIDEBAR_ITEM_TYPES.WIDGETS ? 0 : 20
        }}
      >
        {isOnboarding ? (
          <>
            {activeItem === SIDEBAR_ITEM_TYPES.TEMPLATES && (
              <OnboardingTemplatesPanel onTemplateDragStart={onDragTemplateStart} />
            )}
            {activeItem === SIDEBAR_ITEM_TYPES.WIDGETS && <OnboardingWidgetsPanel onItemDragStart={onDragStart} />}
            {activeItem === SIDEBAR_ITEM_TYPES.BUILDER && <BuilderPanel onItemDragStart={onDragStart} />}
            {activeItem === SIDEBAR_ITEM_TYPES.SCHEDULE && <SchedulePanel />}
          </>
        ) : (
          <>
            {activeItem === SIDEBAR_ITEM_TYPES.TEXT && <SidebarTextPanel onItemDragStart={onDragStart} />}
            {activeItem === SIDEBAR_ITEM_TYPES.IMAGES && <SidebarImagesPanel onItemDragStart={onDragStart} />}
            {activeItem === SIDEBAR_ITEM_TYPES.WIDGETS && <WidgetsPanel onItemDragStart={onDragStart} />}
            {activeItem === SIDEBAR_ITEM_TYPES.BUILDER && <BuilderPanel onItemDragStart={onDragStart} />}
            {activeItem === SIDEBAR_ITEM_TYPES.TEMPLATES && (
              <TemplatesPanel onTemplateDragStart={onDragTemplateStart} />
            )}
            {activeItem === SIDEBAR_ITEM_TYPES.SCHEDULE && <SchedulePanel />}
            {activeItem === SIDEBAR_ITEM_TYPES.TAGS && <SidebarTagsPanel />}
          </>
        )}
      </div>
    </div>
  );
};
